import React from 'react';
import './App.css';
import verilexLogo from './logo-with-text/logo-with-text-white.svg';
import CaseDashboard from './components/CaseDashboard';
import { useAppLogic } from './components/useAppLogic';

// Components
import PDFEditorCanvas from './components/PDFEditorCanvas';
import PDFThumbnails from './components/PDFThumbnails';
import EditorToolbar from './components/EditorToolbar';
import Sidebar from './components/Sidebar';
import axios from 'axios'; 

// UI Icons
import { FileText, UploadCloud, Loader, PanelLeftIcon } from './components/Icons';
import { API_BASE_URL } from './config';

export default function App() {
  const {
    // --- State Variables from Hook ---
    sessionId, setSessionId,
    projectName, setProjectName,
    currentView, setCurrentView,
    activeItem, setActiveItem,
    sections, setSections,
    uploadedSections, setUploadedSections,
    skippedSections, 
    setViewingFileUrl,
    sectionFiles,
    viewingFileUrl,
    indexFile, setIndexFile,
    indexFileName,
    isUploading,
    extractionStatus,
    extractionProgress,
    merging,
    mergedDocument,
    isDragging,
    isReordering,
    isModifying,
    scale, setScale,
    pageRotations,
    selectedPage, setSelectedPage,
    numPages, setNumPages,
    overlays,
    selectedOverlayId, setSelectedOverlayId,
    showPageNumbers,
    showPageNumbersForActiveFile,
    pageNumberPosition, setPageNumberPosition,
    pageNumberFontSize,        // ADD
    setPageNumberFontSize,  
    isThumbnailsOpen,
    imageInputRef,
    loadExistingSession,

    // --- Computed Values ---
    activeName,
    isExtracting,
    canMerge,
    calculateOverallProgress,
    pageOffset,
    currentPreviewUrl,

    // --- Action Handlers ---
    handleIndexUpload,
    handleMergeDocuments,
    handleSectionDelete,
    handleAddSection,
    handleOnDragEnd,
    handleUpdateOverlay,
    handleDeleteOverlay,
    handleImageSelected,
    triggerImageUpload,
    handleInsertPageLocally,
    handleDeleteCurrentPage,
    handleDeleteFile,
    onAddBlankPageWrapper,
    handleAppendUploadWrapped,
    toggleThumbnails,
    togglePageNumbers,
    handleRotateCurrentPage,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    jurisdiction, setJurisdiction,
  } = useAppLogic();

  const calculatePageOffset = (currentFileId) => {
  let offset = 0;
  // Iterate through your sections in order
  for (const section of sections) {
    if (section.id === currentFileId) break;
    // Add the page count of completed sections to the offset
    offset += section.pageCount || 0; 
  }
  return offset;
};

const getGlobalPageOffset = (selectedFilename) => {
  let offset = 0;
  
  // Ensure we are using the actual state variable 'sections'
  if (!sections || !Array.isArray(sections)) return 0;

  for (const section of sections) {
    // Check if the current file is inside this section's files array
    const hasFile = section.files && section.files.includes(selectedFilename);
    
    // If we found the file we are currently viewing, stop adding to the offset
    if (hasFile) break;

    // Add the pages of all previous sections to the offset
    // This assumes your backend provides 'page_count' for each section
    offset += section.page_count || 0; 
  }
  
  return offset;
};

  // Resolve which URL to show in the viewer
  const activeUrl = activeItem.type === 'section' ? viewingFileUrl : currentPreviewUrl;

  // Add this handler alongside your others in App.js
const handleNewFiling = () => {
  window.location.reload();
};

const handleOpenCase = async (caseSessionId) => {
  await loadExistingSession(caseSessionId);
  setCurrentView('editor');
};

  return (
    <>
      {/* 1. DASHBOARD VIEW (Conditional) */}
      {currentView === 'dashboard' ? (
        <CaseDashboard 
        onBackToEditor={() => setCurrentView('editor')}
        onNewFiling={handleNewFiling}
        onOpenCase={handleOpenCase}
        />
      ) : (
        /* 2. MAIN EDITOR VIEW */
        <div className="app-container">

          {/* LEFT SIDEBAR: Navigation & File List */}
          <Sidebar
            projectName={projectName}
            setProjectName={setProjectName}
            logo={verilexLogo}
            isExtracting={isExtracting}
            extractionStatus={extractionStatus}
            extractionProgress={extractionProgress}
            activeItem={activeItem}
            jurisdiction={jurisdiction}
            setJurisdiction={setJurisdiction}
            setActiveItem={setActiveItem}
            indexFile={indexFile}
            sections={sections}
            uploadedSections={uploadedSections}
            skippedSections={skippedSections}
            merging={merging}
            mergedDocument={mergedDocument}
            canMerge={canMerge}
            calculateOverallProgress={calculateOverallProgress}
            handleOnDragEnd={handleOnDragEnd}
            handleSectionDelete={handleSectionDelete}
            handleAddSection={handleAddSection}
            handleMergeDocuments={handleMergeDocuments}
            sectionFiles={sectionFiles}
            onDeleteFile={handleDeleteFile}
            viewingFileUrl={viewingFileUrl}
            onUpdateOverlay={handleUpdateOverlay}
            onFileClick={(filename) => {
            const newUrl = `${API_BASE_URL}/api/view/${sessionId}/${filename}`;
            setViewingFileUrl(newUrl);
            setSelectedPage(1);
          }}
          />

          <main className="main-pane">
            <div
              className={`content-area ${isDragging ? 'drag-active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* REORDERING OVERLAY */}
              {isReordering ? (
                <div className="upload-placeholder">
                  <div className="placeholder-content">
                    <div className="icon-large"><Loader size={48} /></div>
                    <h3>Reordering sections...</h3>
                  </div>
                </div>
              ) : activeUrl ? (
                /* PDF EDITOR VIEWPORT */
                <div className="editor-layout">
                  <div className="pdf-viewport">
                    <div className="pdf-split-container">

                      {/* PDF THUMBNAILS (LEFT STRIP) */}
                      <PDFThumbnails
                        fileUrl={activeUrl}
                        selectedPage={selectedPage}
                        onPageClick={setSelectedPage}
                        isOpen={isThumbnailsOpen}
                      />

                      {/* MAIN CANVAS AREA */}
                      <div className="main-canvas-area">
                        {/* SIDEBAR TOGGLE BUTTON */}
                        <button
                          className="btn-toggle-sidebar"
                          onClick={toggleThumbnails}
                          title={isThumbnailsOpen ? "Close Thumbnails" : "Open Thumbnails"}
                        >
                          <PanelLeftIcon size={20} />
                        </button>

                        {/* ACTUAL PDF CANVAS */}
                        <div className="canvas-scroll-view">
                          <PDFEditorCanvas
                            key={activeUrl}
                            fileUrl={activeUrl}
                            scale={scale}
                            pageRotations={pageRotations}
                            pageNumberPositionsMap={pageNumberPosition}  // the raw { filename: { pageNum: {x,y} } } map
                            currentFilename={
                              activeItem.type === 'section'
                                ? viewingFileUrl?.split('/').pop().split('?')[0]
                                : indexFileName
                            }
                            pageNumberFontSize={pageNumberFontSize}       
                            setPageNumberFontSize={setPageNumberFontSize} 
                            selectedPage={selectedPage}
                            onPageClick={setSelectedPage}
                            onDocumentLoadSuccess={({ numPages }) => {
                              setNumPages(numPages);
                            }}
                            onZoom={setScale}
                            sessionId={sessionId}
                            overlays={overlays}
                            currentFilename={
                              activeItem.type === 'index'
                                ? indexFileName
                                : (uploadedSections[activeItem.index]?.name || "")
                            }
                            selectedOverlayId={selectedOverlayId}
                            onSelectOverlay={setSelectedOverlayId}
                            onUpdateOverlay={handleUpdateOverlay}
                            onDeleteOverlay={handleDeleteOverlay}
                            pageNumberPosition={pageNumberPosition}
                            setPageNumberPosition={setPageNumberPosition}
                            showPageNumbers={showPageNumbersForActiveFile}
                            pageOffset={pageOffset}
                            apiBaseUrl={API_BASE_URL}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* EMPTY STATE / UPLOAD PLACEHOLDERS */
                <div className="upload-placeholder">
                  {/* MASTER INDEX UPLOAD */}
                  {activeItem.type === 'index' && !indexFile && (
                    <div className="placeholder-content">
                      <div className="icon-large">
                        {isUploading ? <Loader size={48} /> : <UploadCloud size={48} />}
                      </div>
                      <h3>{isUploading ? extractionStatus : 'Upload Master Index'}</h3>
                      {!isUploading && (
                        <label className="btn-upload">
                          Select File
                          <input
                            type="file"
                            onChange={(e) => handleIndexUpload(e.target.files[0])}
                            disabled={isUploading}
                            hidden
                          />
                        </label>
                      )}
                    </div>
                  )}

                  {/* SECTION FILE UPLOAD */}
                  {activeItem.type === 'section' && (
                    <div className="placeholder-content">
                      <div className="icon-large">
                        {isUploading ? <Loader size={48} /> : <FileText size={48} />}
                      </div>
                      <h3>{isUploading ? 'Uploading Section...' : 'Upload Section File'}</h3>
                      {!isUploading && <p>Drag and drop or select the PDF for this section.</p>}
                      {!isUploading && (
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '16px' }}>
                          <label className="btn-upload">
                            Select Document
                            <input
                              type="file"
                              accept=".pdf, .doc, .docx"
                              onChange={(e) => handleAppendUploadWrapped(e.target.files[0])}
                              hidden
                            />
                          </label>
                          <button
                            className="btn-upload"
                            onClick={onAddBlankPageWrapper}
                            disabled={isModifying}
                          >
                            {isModifying ? <Loader size={18} /> : "Upload Blank Page"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </main>

          {/* RIGHT VERTICAL TOOLBAR: Page Tools & Settings */}
          <EditorToolbar
            showTools={!!activeUrl}
            projectName={projectName}
            scale={scale}
            setScale={setScale}
            onRotate={handleRotateCurrentPage}
            selectedPage={selectedPage}
            numPages={numPages}
            triggerImageUpload={triggerImageUpload}
            isModifying={isModifying}
            currentPreviewUrl={activeUrl}
            imageInputRef={imageInputRef}
            handleImageSelected={handleImageSelected}
            onInsertBlankPage={handleInsertPageLocally}
            onAppendUpload={handleAppendUploadWrapped}
            onDeletePage={handleDeleteCurrentPage}
            showAddButtons={activeItem.type === 'section'}
            showPageNumbers={showPageNumbers}
            togglePageNumbers={togglePageNumbers}
            onProfileClick={() => setCurrentView('dashboard')}
          />
        </div>
      )}
    </>
  );
}